import { noteNameToMidi } from '../music/theory';
import { DEFAULT_PITCH, INSTRUMENT_LABELS, defaultAudioBus, defaultTrackSettings, sceneEditorDefaults } from './factories';
import { V1MusicSchema, type V1Track } from './legacy/v1';
import { CURRENT_SCHEMA_VERSION, INSTRUMENTS, ProjectSchema, formatIssues, type Instrument, type NoteEvent, type Project } from './schemas';

/**
 * Backward-compatible migration hooks.
 *
 * Each entry upgrades a raw document from version N to N+1. Migrations operate on
 * untrusted `unknown` data and must never throw for well-formed old documents; the
 * fully migrated result is always validated against the current `ProjectSchema`.
 *
 * To introduce a new version: bump CURRENT_SCHEMA_VERSION, update the schemas, add
 * `N: (doc) => ({ ...doc, schemaVersion: N + 1, ...changes })` here plus a fixture test.
 */
export type Migration = (doc: Record<string, unknown>) => Record<string, unknown>;

export const MIGRATIONS: Readonly<Record<number, Migration>> = {
  // v0: pre-release prototype documents had no schemaVersion, a top-level `bpm`,
  // and no generation history. Upgrade them to v1.
  0: (doc) => {
    const { bpm, ...rest } = doc as { bpm?: unknown } & Record<string, unknown>;
    const settings = (typeof rest.settings === 'object' && rest.settings) || {};
    return {
      ...rest,
      schemaVersion: 1,
      settings: { bpm: typeof bpm === 'number' ? bpm : 96, swing: 0, masterVolumeDb: -6, ...settings },
      assets: Array.isArray(rest.assets) ? rest.assets : [],
      generationRequests: Array.isArray(rest.generationRequests) ? rest.generationRequests : [],
      outputs: Array.isArray(rest.outputs) ? rest.outputs : [],
    };
  },
  // v1 (Phase 0) → v2 (Phase 1 music engine). See migrateV1ToV2.
  1: (doc) => migrateV1ToV2(doc),
  // v2 (Phase 1) → v3 (Phase 2 audio production): tracks gain an empty sample slot,
  // and the project gains an empty clip timeline and a default audio bus. Nothing is
  // removed; existing uploaded assets keep their ids and binaries.
  2: (doc) => ({
    ...doc,
    schemaVersion: 3,
    tracks: Array.isArray(doc.tracks)
      ? doc.tracks.map((t: unknown) =>
          typeof t === 'object' && t !== null && !('sample' in t) ? { ...t, sample: null } : t,
        )
      : doc.tracks,
    audioClips: Array.isArray(doc.audioClips) ? doc.audioClips : [],
    audioBus: typeof doc.audioBus === 'object' && doc.audioBus !== null ? doc.audioBus : defaultAudioBus(),
  }),
  // v3 (Phase 2) → v4 (Phase 3A scene editor): every scene gains a 1280×720 canvas, a
  // plain background, one empty layer and no objects; the project gains an empty
  // character library. Phase 0 animated shapes, music and audio are untouched.
  3: (doc) => ({
    ...doc,
    schemaVersion: 4,
    scenes: Array.isArray(doc.scenes)
      ? doc.scenes.map((scene: unknown) => {
          if (typeof scene !== 'object' || scene === null) return scene;
          const s = scene as Record<string, unknown>;
          // Deterministic layer id so migrating twice gives identical output.
          const layerId = `lyr_${typeof s.id === 'string' ? s.id : 'scene'}`.slice(0, 64);
          return { ...sceneEditorDefaults(layerId), ...s };
        })
      : doc.scenes,
    characters: Array.isArray(doc.characters) ? doc.characters : [],
  }),
  // v4 (Phase 3A) → v5 (Phase 3B keyframe animation): the project gains an empty list of
  // animations. Scenes, objects, characters, Phase 0 shapes, music and audio are
  // untouched; character instances without a pose use the neutral pose.
  4: (doc) => ({
    ...doc,
    schemaVersion: 5,
    animations: Array.isArray(doc.animations) ? doc.animations : [],
  }),
  // v5 (Phase 3C) → v6 (Phase 4A audio-visual sync): the project gains an empty list of
  // audio-visual sessions. Music, audio, scenes, characters and animations (including
  // keyframes generated by procedural motion) are untouched.
  5: (doc) => ({
    ...doc,
    schemaVersion: 6,
    avSessions: Array.isArray(doc.avSessions) ? doc.avSessions : [],
  }),
  // v6 (Phase 4A/4B) → v7 (Phase 4C music visualizers): every audio-visual session gains an
  // empty visualizer (no layers). Nothing else changes.
  6: (doc) => ({
    ...doc,
    schemaVersion: 7,
    avSessions: Array.isArray(doc.avSessions)
      ? doc.avSessions.map((s: unknown) => (s && typeof s === 'object' && !Array.isArray(s) && !('visualizer' in s) ? { ...s, visualizer: { layers: [] } } : s))
      : [],
  }),
  // v7 (Phase 4C) → v8 (Phase 5B.1 Look v1): every new field (gradients, glow, shadow,
  // layer opacity and blend, scene look) is optional and absent means the previous
  // rendering, so the data is unchanged. The version bump makes older builds refuse a
  // v8 project instead of silently dropping its Look fields.
  7: (doc) => ({ ...doc, schemaVersion: 8 }),
};

const V1_INSTRUMENT_MAP: Record<V1Track['instrument'], Instrument> = {
  kick: 'kick',
  snare: 'snare',
  hats: 'hats',
  bass: 'bass808',
};

/**
 * Phase 0 projects had four tracks, each holding one 16-step clip of on/off steps.
 * They become: one pattern holding the same hits as notes (bass note names → MIDI),
 * one arrangement section that loops it, the remaining instruments as empty tracks,
 * and pattern-loop transport so playback behaves exactly as before. Track names,
 * volumes and mutes, tempo, swing, master volume, scenes, assets and generation
 * history are preserved.
 */
export function migrateV1ToV2(doc: Record<string, unknown>): Record<string, unknown> {
  const parsed = V1MusicSchema.safeParse(doc);
  if (!parsed.success) throw new MigrationError('Phase 0 project data is invalid.', formatIssues(parsed.error));
  const v1 = parsed.data;
  const tracks: Record<string, unknown>[] = [];
  const notes: Partial<Record<Instrument, NoteEvent[]>> = {};
  for (const track of v1.tracks) {
    const instrument = V1_INSTRUMENT_MAP[track.instrument];
    if (tracks.some((t) => t.instrument === instrument)) continue; // v1 allowed duplicates; keep the first
    const defaults = defaultTrackSettings(instrument);
    tracks.push({
      id: track.id,
      name: track.name || INSTRUMENT_LABELS[instrument],
      instrument,
      volumeDb: track.volumeDb,
      pan: 0,
      muted: track.muted,
      solo: false,
      // Keep Phase 0's clean sound: no drive was applied then.
      effects: { ...defaults.effects, drive: 0 },
    });
    const steps = track.clips[0]?.pattern.steps ?? [];
    notes[instrument] = steps.flatMap((step, i) =>
      step.active
        ? [
            {
              step: i,
              length: 1,
              velocity: Math.max(0.05, step.velocity),
              pitch: (step.note ? noteNameToMidi(step.note) : undefined) ?? DEFAULT_PITCH[instrument],
              offset: 0,
            },
          ]
        : [],
    );
  }
  let counter = 0;
  const id = (prefix: string) => `${prefix}_v1m${++counter}`;
  for (const instrument of INSTRUMENTS) {
    if (tracks.some((t) => t.instrument === instrument)) continue;
    tracks.push({ id: id('trk'), name: INSTRUMENT_LABELS[instrument], instrument, muted: false, solo: false, ...defaultTrackSettings(instrument) });
  }
  tracks.sort((a, b) => INSTRUMENTS.indexOf(a.instrument as Instrument) - INSTRUMENTS.indexOf(b.instrument as Instrument));
  const patternId = id('pat');
  return {
    ...doc,
    schemaVersion: 2,
    settings: { ...v1.settings, key: { root: 4, scale: 'minor' } },
    transport: { mode: 'pattern', loopSong: false },
    tracks,
    patterns: [{ id: patternId, name: 'Phase 0 groove', lengthSteps: 16, tracks: notes }],
    activePatternId: patternId,
    arrangement: { sections: [{ id: id('sec'), name: 'Loop', kind: 'verse', patternId, bars: 4 }] },
    generator: null,
  };
}

export class MigrationError extends Error {
  readonly details: string[];
  constructor(message: string, details: string[] = []) {
    super(message);
    this.name = 'MigrationError';
    this.details = details;
  }
}

function readVersion(doc: Record<string, unknown>): number {
  const v = doc.schemaVersion;
  if (v === undefined) return 0;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 0) {
    throw new MigrationError('Project has an invalid schemaVersion.');
  }
  return v;
}

/** Upgrades and validates an untrusted project document. Throws MigrationError on failure. */
export function migrateProjectDocument(
  input: unknown,
  migrations: Readonly<Record<number, Migration>> = MIGRATIONS,
  targetVersion: number = CURRENT_SCHEMA_VERSION,
): Project {
  if (typeof input !== 'object' || input === null || Array.isArray(input)) {
    throw new MigrationError('Project data must be a JSON object.');
  }
  let doc = { ...(input as Record<string, unknown>) };
  let version = readVersion(doc);
  if (version > targetVersion) {
    throw new MigrationError(
      `This project was saved by a newer version of SonicForge (schema v${version}); this build supports up to v${targetVersion}.`,
    );
  }
  while (version < targetVersion) {
    const migrate = migrations[version];
    if (!migrate) throw new MigrationError(`No migration available from schema v${version}.`);
    try {
      doc = migrate(doc);
    } catch (error) {
      if (error instanceof MigrationError) throw error;
      throw new MigrationError(`Could not upgrade the project from schema v${version}.`, [String(error)]);
    }
    const next = readVersion(doc);
    if (next !== version + 1) throw new MigrationError(`Migration from v${version} produced v${next}.`);
    version = next;
  }
  const parsed = ProjectSchema.safeParse(doc);
  if (!parsed.success) {
    throw new MigrationError('Project data failed validation.', formatIssues(parsed.error));
  }
  return parsed.data;
}
